import { useEffect, useState } from "react";
import { Check, ChevronDown, Minus, Plus, X } from "lucide-react";
import { api } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const Selected = ({ items, value, setQty, remove }) => (
  <div className="mt-2 divide-y border" data-testid="tech-services-selected">
    {value.map((s) => {
      const i = items.find((x) => x.code === s.code);
      return (
        <div key={s.code} className="flex items-center gap-1 bg-emerald-50 px-2 py-1.5 text-sm" data-testid={`tech-service-selected-${s.code}`}>
          <span className="flex-1"><span className="font-mono text-xs">{s.code}</span> {i?.description}</span>
          <button type="button" className="p-2" onClick={() => setQty(s.code, s.qty - 1)} aria-label="Meno" data-testid={`tech-service-minus-${s.code}`}><Minus className="h-4 w-4" /></button>
          <span className="w-7 text-center font-mono" data-testid={`tech-service-qty-${s.code}`}>{s.qty}</span>
          <button type="button" className="p-2" onClick={() => setQty(s.code, s.qty + 1)} aria-label="Più" data-testid={`tech-service-plus-${s.code}`}><Plus className="h-4 w-4" /></button>
          <button type="button" className="p-2 text-muted-foreground" onClick={() => remove(s.code)} aria-label="Togli" data-testid={`tech-service-remove-${s.code}`}><X className="h-4 w-4" /></button>
        </div>
      );
    })}
  </div>
);

const Option = ({ i, on, toggle, testId }) => (
  <button type="button" onClick={() => toggle(i.code)} className="flex w-full items-center gap-2 rounded-sm px-2 py-2.5 text-left text-sm hover:bg-accent" data-testid={testId}>
    <span className={`flex h-5 w-5 shrink-0 items-center justify-center border ${on ? "border-emerald-600 bg-emerald-600 text-white" : "border-zinc-400"}`}>{on && <Check className="h-3.5 w-3.5" />}</span>
    <span>{i.code} — {i.description}</span>
  </button>
);

const SUPPLEMENTS = { "D-OP.CIV.2": { code: "D-OP.CIV.3", label: "Metri di scavo oltre i primi 10 m" } };

const Excess = ({ base, items, value, onChange }) => {
  const sup = SUPPLEMENTS[base];
  if (!items.some((i) => i.code === sup.code)) return null;
  const cur = value.find((s) => s.code === sup.code)?.qty || "";
  const set = (v) => {
    const m = Math.min(99, Math.max(0, Math.round(Number(v) || 0)));
    const rest = value.filter((s) => s.code !== sup.code);
    onChange(m ? [...rest, { code: sup.code, qty: m }] : rest);
  };
  return (
    <div className="mt-2 border border-amber-300 bg-amber-50 p-3" data-testid={`tech-service-excess-${base}`}>
      <Label className="micro-label">{sup.label} <span className="normal-case tracking-normal text-muted-foreground">· voce {sup.code} al metro</span></Label>
      <input type="number" inputMode="numeric" min="0" max="99" className="mt-1.5 h-12 w-full border bg-white px-3 text-base" placeholder="0 se lo scavo è entro 10 m"
        value={cur} onChange={(e) => set(e.target.value)} data-testid={`tech-service-excess-input-${base}`} />
    </div>
  );
};

export const TechServices = ({ orderId, value, onChange }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  useEffect(() => { api.get(`/tech/orders/${orderId}/services`).then((r) => setData(r.data)).catch(() => setData({ price_list: null, items: [] })); }, [orderId]);
  const sel = new Set(value.map((s) => s.code));
  const toggle = (code) => onChange(sel.has(code) ? value.filter((s) => s.code !== code && s.code !== SUPPLEMENTS[code]?.code) : [...value, { code, qty: 1 }]);
  const setQty = (code, qty) => onChange(value.map((s) => (s.code === code ? { ...s, qty: Math.min(99, Math.max(1, qty)) } : s)));
  if (!data) return <div className="text-sm text-muted-foreground">Caricamento prestazioni…</div>;
  if (!data.price_list) return <p className="text-sm text-red-700" data-testid="tech-services-none">Nessun listino valido per questo intervento: avvisa l'assistente tecnico.</p>;
  const label = value.length === 0 ? "Seleziona" : value.length === 1 ? data.items.find((i) => i.code === value[0].code)?.description : `${value.length} prestazioni selezionate`;
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  const shown = data.items.filter((i) => words.every((w) => `${i.code} ${i.description}`.toLowerCase().includes(w))).slice(0, 80);
  const frequent = words.length ? [] : (data.frequent || []).map((c) => data.items.find((i) => i.code === c)).filter(Boolean);
  return (
    <div data-testid="tech-services">
      <Label className="micro-label">Prestazioni * <span className="normal-case tracking-normal text-muted-foreground">· {data.price_list.name}</span></Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" className="mt-1.5 flex h-12 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 text-left text-base shadow-sm" data-testid="tech-services-select">
            <span className={`truncate ${value.length ? "" : "text-muted-foreground"}`} data-testid="tech-services-count">{label}</span>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="max-h-96 w-[--radix-popover-trigger-width] overflow-y-auto p-1" data-testid="tech-services-options">
          <div className="sticky top-0 z-10 bg-popover p-1">
            <input className="h-11 w-full border px-3 text-base" placeholder={`Cerca tra ${data.items.length} voci (codice o descrizione)`} value={q} onChange={(e) => setQ(e.target.value)} data-testid="tech-services-search" />
          </div>
          {frequent.length > 0 && (
            <div className="mb-1 border-b pb-1" data-testid="tech-services-frequent">
              <div className="px-2 pt-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Più usate su questa commessa</div>
              {frequent.map((i) => <Option key={i.code} i={i} on={sel.has(i.code)} toggle={toggle} testId={`tech-service-frequent-${i.code}`} />)}
              <div className="px-2 pt-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Tutte le voci</div>
            </div>
          )}
          {shown.map((i) => <Option key={i.code} i={i} on={sel.has(i.code)} toggle={toggle} testId={`tech-service-${i.code}`} />)}
          {!shown.length && <p className="px-2 py-3 text-sm text-muted-foreground" data-testid="tech-services-empty">Nessuna voce trovata.</p>}
          {shown.length === 80 && <p className="px-2 py-1 text-xs text-muted-foreground">Mostrate le prime 80: affina la ricerca.</p>}
          <button type="button" onClick={() => setOpen(false)} className="mt-1 w-full border-t py-2.5 text-sm font-semibold" data-testid="tech-services-done">Fatto</button>
        </PopoverContent>
      </Popover>
      {value.length > 0 && <Selected items={data.items} value={value} setQty={setQty} remove={toggle} />}
      {Object.keys(SUPPLEMENTS).filter((b) => sel.has(b)).map((b) => <Excess key={b} base={b} items={data.items} value={value} onChange={onChange} />)}
    </div>
  );
};
